import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { ContentBlock } from '../LayoutPrimitives';
import Button from '../Button';
import MediaGalleryModal from '../MediaGalleryModal';
import { IconPhotoGallery } from '../icons/Icons';

import LayoutSm from './LayoutSm';
import LayoutMd from './LayoutMd';
import LayoutXl from './LayoutXl';
import { PHOTO_OBJECT_TYPE } from './types';

import { useWindowSize } from '../../../hooks';
import { BREAKPOINTS, STATUS } from '../../../constants';

const { IDLE, PENDING, SUCCESS, ERROR } = STATUS;

export default function HeroImageGallery({
    galleryImages = [],
    heroImage = {},
    status,
    hasThumbnailStrip,
    className,
}) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { width = 0 } = useWindowSize();
    const images = Array.isArray(galleryImages) ? [...galleryImages] : [];

    if (!isEmpty(heroImage)) {
        images.unshift(heroImage);
    }

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    // RENDER LOGIC
    //
    // Case: API request is not finished - show grey box
    if (status === PENDING || status === IDLE) {
        return (
            <div className={cx('sarsa-hero-image-gallery', className)}>
                <LayoutSm />
            </div>
        );
    }

    // Case: API error, or success but no images - show unavailable image
    if (status === ERROR || !images || images.length === 0) {
        return (
            <div className={cx('sarsa-hero-image-gallery', className)}>
                <LayoutSm showUnavailableImage />
            </div>
        );
    }

    // Case: Less than 3 images - show single image
    if (images.length < 3) {
        return (
            <div className={cx('sarsa-hero-image-gallery', className)}>
                <LayoutSm image={images[0]} />
            </div>
        );
    }

    // Case: HAPPY PATH 3+ IMAGES!
    return (
        <div>
            <div className={cx('sarsa-hero-image-gallery', className)}>
                {width >= BREAKPOINTS.xl && <LayoutXl images={images} />}

                {width < BREAKPOINTS.xl && width >= BREAKPOINTS.md && (
                    <LayoutMd images={images} />
                )}

                {width < BREAKPOINTS.md && <LayoutSm image={images[0]} />}

                <ContentBlock>
                    <Button
                        size="xs"
                        appearance="solid-white"
                        iconBeforeElement={<IconPhotoGallery />}
                        onClick={openModal}
                        className="view-more-button"
                    >
                        View Photos
                    </Button>
                </ContentBlock>
            </div>

            <MediaGalleryModal
                images={galleryImages}
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                activeIndex={0}
                hasThumbnailStrip={hasThumbnailStrip}
            />
        </div>
    );
}

HeroImageGallery.propTypes = {
    galleryImages: PropTypes.arrayOf(PHOTO_OBJECT_TYPE),
    heroImage: PHOTO_OBJECT_TYPE,

    /** API fetch status */
    status: PropTypes.oneOf([IDLE, PENDING, ERROR, SUCCESS]),

    /** Shows the thumbnail strip at the bottom of the open gallery */
    hasThumbnailStrip: PropTypes.bool,

    /** Additional className added to the outer container */
    className: PropTypes.string,
};

HeroImageGallery.defaultProps = {
    hasThumbnailStrip: true,
};
